import { Component, DestroyRef, effect, ElementRef, inject, input, signal, ViewChild } from '@angular/core';
import { renderAsync } from 'docx-preview';
import { PdfViewerComponent } from '../pdf-viewer/pdf-viewer.component';

@Component({
  selector: 'lib-doc-preview',
  imports: [PdfViewerComponent],
  standalone: true,
  templateUrl: './doc-preview.component.html',
  styleUrl: './doc-preview.component.scss'
})
export class DocPreviewComponent {
  article = input.required<string>();

  @ViewChild('docxContainer') docxContainer!: ElementRef;

  isLoading = false;
  error = '';
  pdfDisplayUrl = signal<string | null>(null);

  /** Blob URL created for external PDFs (e.g. S3 signed URLs with attachment disposition). Revoked on cleanup. */
  #pdfBlobUrl: string | null = null;
  readonly #destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const url = this.article();
      if (url) {
        this.loadDocumentFromUrl(url);
      }
    });

    this.#destroyRef.onDestroy(() => {
      this.#revokePdfBlobUrl();
    });
  }

  #revokePdfBlobUrl(): void {
    if (this.#pdfBlobUrl) {
      URL.revokeObjectURL(this.#pdfBlobUrl);
      this.#pdfBlobUrl = null;
    }
  }

  /** Returns a URL suitable for inline PDF display. External PDF URLs (e.g. S3 with attachment) are fetched and converted to a blob URL so the browser displays instead of downloading. */
  async #getPdfDisplayUrl(url: string): Promise<string> {
    const isExternalPdf =
      (url.startsWith('http://') || url.startsWith('https://')) &&
      (url.toLowerCase().includes('.pdf') || url.includes('application/pdf'));

    if (!isExternalPdf) {
      return url;
    }

    this.#revokePdfBlobUrl();
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load PDF: ${response.statusText}`);
    }
    const blob = await response.blob();
    this.#pdfBlobUrl = URL.createObjectURL(blob);
    return this.#pdfBlobUrl;
  }

  #showPdf(displayUrl: string): void {
    this.pdfDisplayUrl.set(displayUrl);
  }

  async loadDocumentFromUrl(url: string) {
    this.#revokePdfBlobUrl();
    this.pdfDisplayUrl.set(null);
    this.isLoading = true;
    this.error = '';

    try {
      const isPdf = url.toLowerCase().endsWith('.pdf') || url.includes('application/pdf');

      if (url.startsWith('blob:')) {
        try {
          const response = await fetch(url);
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/pdf')) {
            this.#showPdf(url);
            return;
          }
        } catch {
          // Fall through to DOCX handling.
        }
      } else if (isPdf) {
        const displayUrl = await this.#getPdfDisplayUrl(url);
        this.#showPdf(displayUrl);
        return;
      }

      if (!this.docxContainer) {
        setTimeout(() => this.loadDocumentFromUrl(url), 100);
        return;
      }

      const docResponse = await fetch(url);
      if (!docResponse.ok) {
        throw new Error(`Failed to load document: ${docResponse.statusText}`);
      }

      const responseContentType = docResponse.headers.get('content-type') || '';
      if (responseContentType.includes('application/pdf')) {
        this.#revokePdfBlobUrl();
        const blob = await docResponse.blob();
        this.#pdfBlobUrl = URL.createObjectURL(blob);
        this.#showPdf(this.#pdfBlobUrl);
        return;
      }

      const arrayBuffer = await docResponse.arrayBuffer();
      this.docxContainer.nativeElement.innerHTML = '';

      await renderAsync(arrayBuffer, this.docxContainer.nativeElement, undefined, {
        className: 'docx-viewer',
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        breakPages: true,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true
      });
    } catch (error) {
      console.error('Error loading document:', error);
      this.error = 'Failed to load document. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  async renderDocument(file: File) {
    this.isLoading = true;
    this.error = '';
    this.pdfDisplayUrl.set(null);

    try {
      const arrayBuffer = await file.arrayBuffer();

      if (!this.docxContainer) {
        throw new Error('Document container not ready');
      }

      this.docxContainer.nativeElement.innerHTML = '';

      await renderAsync(arrayBuffer, this.docxContainer.nativeElement, undefined, {
        className: 'docx-viewer',
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false
      });
    } catch (error) {
      console.error('Error rendering document:', error);
      this.error = 'Failed to render document';
    } finally {
      this.isLoading = false;
    }
  }
}
